import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

function uid() {
  return "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const ChatWidget = () => {
  const { content } = useLanguage();
  const c = content.ui.chat;
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(uid());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, started]);

  const canStart = form.firstName && form.lastName && /\S+@\S+\.\S+/.test(form.email);

  const start = () => {
    if (!canStart) return;
    setStarted(true);
    setMessages([{ from: "bot", text: c.intro }]);
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { from: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId.current,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        message: text,
      });
      setMessages((m) => [...m, { from: "bot", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Une erreur est survenue. Merci de réessayer." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        aria-label="Ouvrir le chat"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-brand-green text-white shadow-2xl transition-transform duration-300 hover:scale-105"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] flex h-[520px] w-[90vw] max-w-[380px] flex-col overflow-hidden rounded-lg border border-black/10 bg-white shadow-2xl"
          >
            <div className="bg-brand-green px-6 py-5 text-white">
              <p className="font-serif text-2xl">{c.title}</p>
              <p className="mt-1 text-sm text-white/70">{c.subtitle}</p>
            </div>

            {!started ? (
              <div className="flex flex-1 flex-col justify-center gap-4 p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-brand-gold">{c.namePrompt}</p>
                <input
                  data-testid="chat-firstname-input"
                  placeholder={c.firstName}
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="w-full rounded-none border-b border-brand-grey bg-transparent py-3 focus:border-brand-green focus:outline-none"
                />
                <input
                  data-testid="chat-lastname-input"
                  placeholder={c.lastName}
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="w-full rounded-none border-b border-brand-grey bg-transparent py-3 focus:border-brand-green focus:outline-none"
                />
                <input
                  data-testid="chat-email-input"
                  type="email"
                  placeholder={c.email}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-none border-b border-brand-grey bg-transparent py-3 focus:border-brand-green focus:outline-none"
                />
                <button
                  data-testid="chat-start-button"
                  onClick={start}
                  disabled={!canStart}
                  className="mt-2 rounded-none bg-brand-green py-3 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-brand-black disabled:opacity-40"
                >
                  {content.ui.buttons.startChat}
                </button>
              </div>
            ) : (
              <>
                <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto bg-brand-beige p-5">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                          m.from === "user"
                            ? "rounded-l-lg rounded-tr-lg bg-brand-green text-white"
                            : "rounded-r-lg rounded-tl-lg bg-white text-brand-black shadow-sm"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 border-t border-brand-grey p-3">
                  <input
                    data-testid="chat-message-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={c.placeholder}
                    className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none"
                  />
                  <button
                    data-testid="chat-send-button"
                    onClick={send}
                    disabled={sending}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green text-white transition-colors hover:bg-brand-black disabled:opacity-40"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
